import React, { useEffect, useState } from 'react';
import { TrendingUp, AlertCircle, Loader2, FileText, FileSignature } from 'lucide-react';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';

/**
 * Cash-Flow Forecast widget — available to ALL plans (unlike the AI
 * variant next door). Buckets open unpaid invoices at 100% and open
 * estimates weighted by status (sent 50% / viewed 70% / accepted 100%)
 * into 30 / 60 / 90-day windows so the owner sees what's plausibly
 * coming in the door.
 */
export default function CashFlowWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get('/cashflow/forecast')
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ error: true }); });
    return () => { cancelled = true; };
  }, []);

  const busy = data === null;
  const hasNumbers = data && !data.error && (data.in_30 || data.in_60 || data.in_90);

  return (
    <div data-testid="cashflow-widget" className="relative bg-gradient-to-br from-[#1B2838] to-[#1f2f42] border border-emerald-500/15 rounded-xl p-4 sm:p-5 overflow-hidden">
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-emerald-500/10 blur-3xl rounded-full pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
              <TrendingUp size={13} className="text-[#0D1520]" strokeWidth={2.6} />
            </div>
            <div>
              <p className="text-[12px] font-bold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Cash-Flow Forecast</p>
              <p className="text-[9px] text-[#758A9F]">Estimates + open invoices · next 30 / 60 / 90 days</p>
            </div>
          </div>
          <span className="text-[9px] uppercase tracking-wider text-emerald-400 font-bold">Live</span>
        </div>

        {busy ? (
          <div className="flex items-center gap-2 text-[#758A9F] text-[11px] py-4">
            <Loader2 size={12} className="animate-spin" /> Calculating…
          </div>
        ) : !hasNumbers ? (
          <p className="text-[11px] text-[#758A9F] py-4">No open invoices or estimates yet — send some to see the forecast fill in.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Bucket label="30 days" data={data.in_30} accent="text-emerald-400" />
              <Bucket label="60 days" data={data.in_60} accent="text-[#CBA33B]" />
              <Bucket label="90 days" data={data.in_90} accent="text-blue-400" />
            </div>

            {data.overdue && data.overdue.count > 0 && (
              <button
                type="button"
                onClick={() => navigate('/envoice')}
                data-testid="cashflow-overdue-cta"
                className="w-full flex items-center gap-2 px-2 py-1.5 mb-2 rounded-md bg-red-500/10 border border-red-500/20 hover:bg-red-500/15 transition-colors"
              >
                <AlertCircle size={12} className="text-red-400 flex-shrink-0" />
                <p className="text-[11px] text-red-300 text-left flex-1">
                  <span className="font-bold">${(data.overdue.total || 0).toFixed(2)}</span> overdue across {data.overdue.count} invoice{data.overdue.count !== 1 ? 's' : ''} — chase now
                </p>
              </button>
            )}

            <div className="flex items-center gap-4 text-[10px] text-[#758A9F] mt-2">
              <span className="inline-flex items-center gap-1"><FileText size={10} className="text-emerald-400" /> Invoices at 100%</span>
              <span className="inline-flex items-center gap-1"><FileSignature size={10} className="text-[#CBA33B]" /> Estimates weighted</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Bucket({ label, data, accent }) {
  const total = (data && data.total) || 0;
  const inv = (data && data.invoices) || 0;
  const est = (data && data.estimates) || 0;
  return (
    <div className="bg-white/[0.03] rounded-md px-2 py-2">
      <p className="text-[8px] uppercase tracking-wider text-[#758A9F] font-bold">{label}</p>
      <p className={`text-[14px] font-bold tabular-nums ${accent} mt-0.5`} style={{ fontFamily: "'Outfit', sans-serif" }}>${total.toFixed(0)}</p>
      <div className="text-[8px] text-[#758A9F] mt-0.5 leading-tight">
        <span className="text-emerald-400 font-semibold">${inv.toFixed(0)}</span> inv · <span className="text-[#CBA33B] font-semibold">${est.toFixed(0)}</span> est
      </div>
    </div>
  );
}
